import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import api, { apiErr } from "@/lib/api";
import { Loader, PageHeader, btn } from "@/components/Page";

const MODE_KEY = "overall";

function tierDefaults(config, rank) {
  return config?.leagues?.[MODE_KEY]?.placements?.find((entry) => entry.rank === rank) || { rank, title: "", gold: 0, follis: 0, chest_item_id: "", chest_qty: 0 };
}

export default function LudusTriasAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const { data: response } = await api.get("/triumvirate/admin/rewards");
        setData(response);
      } catch (error) {
        toast.error(apiErr(error, "Could not load Ludus Trias admin settings"));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const updateTier = (rank, field, value) => {
    setData((current) => {
      const next = JSON.parse(JSON.stringify(current));
      const placements = next.reward_config.leagues[MODE_KEY].placements;
      const idx = placements.findIndex((entry) => entry.rank === rank);
      placements[idx][field] = ["gold", "follis", "chest_qty"].includes(field) ? Number(value || 0) : value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { leagues: data.reward_config.leagues };
      const { data: response } = await api.put("/triumvirate/admin/rewards", payload);
      setData((current) => ({ ...current, reward_config: response.reward_config }));
      toast.success("Ludus Trias rewards updated.");
    } catch (error) {
      toast.error(apiErr(error, "Could not save Ludus Trias rewards"));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) return <Loader label="Loading admin settings…" />;

  return (
    <div data-testid="ludus-trias-admin-page" className="space-y-6">
      <PageHeader
        title="Ludus Trias Admin"
        subtitle="Edit monthly season rewards for the unified Ludus Trias live ladder."
        action={<Link data-testid="ludus-trias-admin-back-link" to="/admin" className={btn("ghost")}>Back to Admin</Link>}
      />

      <div data-testid="ludus-trias-admin-meta" className="border border-amber-900/35 bg-zinc-950/70 rounded-sm p-5 text-sm text-zinc-300">
        Current season: <span className="font-cinzel text-amber-200">{data?.current_season?.label}</span>
      </div>

      <div className="space-y-6">
        <section data-testid="ludus-trias-admin-live-ladder" className="border border-amber-900/35 bg-zinc-950/70 rounded-sm p-5">
          <div className="font-cinzel text-2xl text-amber-200">Unified Live Ladder Rewards</div>
          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            {[1, 2, 3].map((rank) => {
              const tier = tierDefaults(data.reward_config, rank);
              return (
                <div key={`overall-${rank}`} data-testid={`ludus-trias-admin-tier-overall-${rank}`} className="border border-zinc-800 bg-zinc-950/45 rounded-sm p-4 space-y-3">
                  <div className="font-cinzel text-lg text-amber-200">Rank #{rank}</div>
                  <label className="block text-sm text-zinc-400">
                    Title
                    <input data-testid={`ludus-trias-admin-title-overall-${rank}`} value={tier.title} onChange={(event) => updateTier(rank, "title", event.target.value)} className="mt-1 w-full rounded-sm border border-zinc-800 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none" />
                  </label>
                  <label className="block text-sm text-zinc-400">
                    Gold
                    <input data-testid={`ludus-trias-admin-gold-overall-${rank}`} type="number" min="0" value={tier.gold} onChange={(event) => updateTier(rank, "gold", event.target.value)} className="mt-1 w-full rounded-sm border border-zinc-800 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none" />
                  </label>
                  <label className="block text-sm text-zinc-400">
                    Follis
                    <input data-testid={`ludus-trias-admin-follis-overall-${rank}`} type="number" min="0" value={tier.follis} onChange={(event) => updateTier(rank, "follis", event.target.value)} className="mt-1 w-full rounded-sm border border-zinc-800 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none" />
                  </label>
                  <label className="block text-sm text-zinc-400">
                    Chest Item
                    <select data-testid={`ludus-trias-admin-chest-overall-${rank}`} value={tier.chest_item_id} onChange={(event) => updateTier(rank, "chest_item_id", event.target.value)} className="mt-1 w-full rounded-sm border border-zinc-800 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none">
                      <option value="">None</option>
                      {(data?.reward_config?.chest_options || []).map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                  </label>
                  <label className="block text-sm text-zinc-400">
                    Chest Qty
                    <input data-testid={`ludus-trias-admin-chest-qty-overall-${rank}`} type="number" min="0" value={tier.chest_qty} onChange={(event) => updateTier(rank, "chest_qty", event.target.value)} className="mt-1 w-full rounded-sm border border-zinc-800 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none" />
                  </label>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <div className="flex flex-wrap gap-3">
        <button data-testid="ludus-trias-admin-save-button" type="button" onClick={save} disabled={saving} className={btn()}>
          {saving ? "Saving…" : "Save Reward Settings"}
        </button>
      </div>
    </div>
  );
}
